import type { Root, Text, PhrasingContent } from 'mdast';
import type { Plugin } from 'unified';

const REF_PATTERN = /@ref\(([^)]+)\)/g;

interface RefNode {
  type: 'refBadge';
  data: {
    hName: string;
    hProperties: { className: string; 'data-ref': string };
    hChildren: Array<{ type: string; value: string }>;
  };
}

/**
 * Remark plugin that transforms @ref(...) text into inline badge nodes.
 * Rendered as: <span class="ref-badge" data-ref="path">📄 path</span>
 */
export const remarkRefPlugin: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(tree);
  };
};

function visit(node: Root | PhrasingContent | { type: string; children?: unknown[] }): void {
  if (!('children' in node) || !Array.isArray(node.children)) return;

  const newChildren: unknown[] = [];
  let changed = false;

  for (const child of node.children as Array<{ type: string; children?: unknown[] }>) {
    if (child.type === 'text') {
      const parts = splitTextByRefs(child as Text);
      if (parts.length > 1) {
        newChildren.push(...parts);
        changed = true;
      } else {
        newChildren.push(child);
      }
    } else {
      visit(child as PhrasingContent);
      newChildren.push(child);
    }
  }

  if (changed) {
    node.children = newChildren;
  }
}

function splitTextByRefs(textNode: Text): Array<Text | RefNode> {
  const parts: Array<Text | RefNode> = [];
  let lastIndex = 0;

  REF_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = REF_PATTERN.exec(textNode.value)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: textNode.value.slice(lastIndex, match.index) } as Text);
    }

    const refArg = match[1];
    const icon = refArg.startsWith('doc_') ? '🔑' : '📄';

    parts.push({
      type: 'refBadge',
      data: {
        hName: 'span',
        hProperties: { className: 'ref-badge', 'data-ref': refArg },
        hChildren: [{ type: 'text', value: `${icon} ${refArg}` }],
      },
    } as unknown as RefNode);

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex === 0) return [textNode];
  if (lastIndex < textNode.value.length) {
    parts.push({ type: 'text', value: textNode.value.slice(lastIndex) } as Text);
  }

  return parts;
}
